/**
 * InsightPro™ — Debtors Page v1.0
 * Data: Debtors table — pre-calculated ageing buckets per account
 * Columns: ACCOUNT, NAME, TOTAL, CURRENT, DAYS_30, DAYS_60, DAYS_90,
 *          DAYS_120, DAYS_150, DAYS_180, ONHOLD, CRLIMIT, DATELASTPAY, AMOUNTLASTPAYMENT
 */
import React, { useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { pbiQuery } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const GOLD  = 'var(--gold)';
const RED   = '#e05555';
const GREEN = '#1aad63';
const AMBER = '#d4a017';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

function usePbi(dax) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await pbiQuery({ dax });
      setData(r?.rows || []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [dax]);
  React.useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

// ── Formatters ─────────────────────────────────────────────
const fmtR = n => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `R ${(v/1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `R ${(v/1_000).toFixed(0)}K`;
  return `R ${v.toFixed(0)}`;
};
const fmtPct = (num, den) => {
  if (!den || den === 0) return '—';
  return `${((num/den)*100).toFixed(1)}%`;
};
const fmtDate = d => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' });
};

function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 0' }}>
      <div style={{ width:28, height:28, border:`3px solid rgba(196,146,42,0.2)`, borderTop:`3px solid ${GOLD}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorBox({ msg, onRetry }) {
  return (
    <div style={{ padding:'24px', textAlign:'center' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:RED, letterSpacing:'0.12em', marginBottom:8 }}>DATA LOAD ERROR</div>
      <div style={{ fontSize:13, color:'var(--page-muted)', marginBottom:16 }}>{msg}</div>
      {onRetry && <button onClick={onRetry} style={{ fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', padding:'7px 16px', background:'none', border:`1px solid ${GOLD}`, color:GOLD, cursor:'pointer', borderRadius:2 }}>RETRY</button>}
    </div>
  );
}

function Card({ children, style }) {
  return <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, ...style }}>{children}</div>;
}

function CardHeader({ title }) {
  return (
    <div style={{ padding:'14px 20px 12px', borderBottom:'1px solid var(--card-border)' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)' }}>{title}</div>
    </div>
  );
}

function KpiCard({ label, value, sub, subColor, alert }) {
  return (
    <div style={{ background:'var(--card)', border:`1px solid ${alert ? 'rgba(224,85,85,0.4)' : 'var(--card-border)'}`, borderRadius:4, padding:'16px 18px', background: alert ? 'rgba(224,85,85,0.04)' : 'var(--card)' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:24, fontWeight:700, color: alert ? RED : 'var(--page-text)', lineHeight:1, marginBottom:6 }}>{value}</div>
      {sub && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: subColor || 'var(--page-muted)' }}>{sub}</div>}
    </div>
  );
}

function BucketBar({ data }) {
  const colors = { 'Current': GREEN, '30 Days': GOLD, '60 Days': AMBER, '90 Days': '#e07040', '120 Days': RED, '150 Days': RED, '180+ Days': '#b03030' };
  const tip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background:'var(--void)', border:'1px solid var(--card-border)', padding:'10px 14px', borderRadius:3 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginBottom:4 }}>{payload[0].payload['[Bucket]']}</div>
        <div style={{ fontSize:13, color:'var(--page-text)' }}>{fmtR(payload[0].value)}</div>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} barCategoryGap="25%">
        <XAxis dataKey="[Bucket]" tick={{ fontFamily:'var(--font-mono)', fontSize:9, fill:'var(--page-muted)' }} axisLine={false} tickLine={false}/>
        <YAxis hide />
        <Tooltip content={tip} />
        <Bar dataKey="[Amount]" radius={[3,3,0,0]}>
          {data.map((entry, i) => (
            <Cell key={i} fill={colors[entry['[Bucket]']] || GOLD} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── DAX queries ─────────────────────────────────────────────
const KPI_DAX = `EVALUATE ROW(
  "TotalBook",     CALCULATE(SUM('Debtors'[TOTAL]),   'Debtors'[TOTAL] > 0),
  "Current",       CALCULATE(SUM('Debtors'[CURRENT]), 'Debtors'[TOTAL] > 0),
  "Days30",        CALCULATE(SUM('Debtors'[DAYS_30]), 'Debtors'[TOTAL] > 0),
  "Days60",        CALCULATE(SUM('Debtors'[DAYS_60]), 'Debtors'[TOTAL] > 0),
  "Days90",        CALCULATE(SUM('Debtors'[DAYS_90]), 'Debtors'[TOTAL] > 0),
  "Days120",       CALCULATE(SUM('Debtors'[DAYS_120]),'Debtors'[TOTAL] > 0),
  "Days150",       CALCULATE(SUM('Debtors'[DAYS_150]),'Debtors'[TOTAL] > 0),
  "Days180Plus",   CALCULATE(SUM('Debtors'[DAYS_180]),'Debtors'[TOTAL] > 0),
  "Overdue60Plus", CALCULATE(SUM('Debtors'[DAYS_60]) + SUM('Debtors'[DAYS_90]) + SUM('Debtors'[DAYS_120]) + SUM('Debtors'[DAYS_150]) + SUM('Debtors'[DAYS_180]), 'Debtors'[TOTAL] > 0),
  "OnHoldCount",   CALCULATE(COUNTROWS('Debtors'), 'Debtors'[ONHOLD] = "Y", 'Debtors'[TOTAL] > 0),
  "ActiveDebtors", CALCULATE(COUNTROWS('Debtors'), 'Debtors'[TOTAL] > 0)
)`;

const BUCKETS_DAX = `EVALUATE
  UNION(
    ROW("Bucket", "Current",   "Amount", CALCULATE(SUM('Debtors'[CURRENT]),  'Debtors'[TOTAL] > 0)),
    ROW("Bucket", "30 Days",   "Amount", CALCULATE(SUM('Debtors'[DAYS_30]),  'Debtors'[TOTAL] > 0)),
    ROW("Bucket", "60 Days",   "Amount", CALCULATE(SUM('Debtors'[DAYS_60]),  'Debtors'[TOTAL] > 0)),
    ROW("Bucket", "90 Days",   "Amount", CALCULATE(SUM('Debtors'[DAYS_90]),  'Debtors'[TOTAL] > 0)),
    ROW("Bucket", "120 Days",  "Amount", CALCULATE(SUM('Debtors'[DAYS_120]), 'Debtors'[TOTAL] > 0)),
    ROW("Bucket", "150 Days",  "Amount", CALCULATE(SUM('Debtors'[DAYS_150]), 'Debtors'[TOTAL] > 0)),
    ROW("Bucket", "180+ Days", "Amount", CALCULATE(SUM('Debtors'[DAYS_180]), 'Debtors'[TOTAL] > 0))
  )`;

const ACCOUNTS_DAX = `EVALUATE
  TOPN(50,
    FILTER(
      SELECTCOLUMNS('Debtors',
        "Account",    'Debtors'[ACCOUNT],
        "Name",       'Debtors'[NAME],
        "Total",      'Debtors'[TOTAL],
        "Current",    'Debtors'[CURRENT],
        "Days30",     'Debtors'[DAYS_30],
        "Days60",     'Debtors'[DAYS_60],
        "Days90",     'Debtors'[DAYS_90],
        "Days120",    'Debtors'[DAYS_120],
        "Days150",    'Debtors'[DAYS_150],
        "Days180",    'Debtors'[DAYS_180],
        "OnHold",     'Debtors'[ONHOLD],
        "CRLimit",    'Debtors'[CRLIMIT],
        "LastPay",    'Debtors'[DATELASTPAY],
        "LastPayAmt", 'Debtors'[AMOUNTLASTPAYMENT],
        "Overdue",    'Debtors'[DAYS_60] + 'Debtors'[DAYS_90] + 'Debtors'[DAYS_120] + 'Debtors'[DAYS_150] + 'Debtors'[DAYS_180]
      ),
      [Total] > 0
    ),
    [Total], DESC
  )
  ORDER BY [Total] DESC`;

// ── Main page ───────────────────────────────────────────────
export default function DebtorsPage() {
  const isMobile = useIsMobile();
  const [view, setView] = useState('overview'); // 'overview' | 'accounts'

  const { data: kpiRaw,  loading: l0, error: e0, reload: r0 } = usePbi(KPI_DAX);
  const { data: buckets, loading: l1, error: e1, reload: r1 } = usePbi(BUCKETS_DAX);
  const { data: accounts,loading: l2, error: e2, reload: r2 } = usePbi(ACCOUNTS_DAX);

  const kpi = Array.isArray(kpiRaw) ? kpiRaw[0] : kpiRaw;
  const totalBook     = kpi?.['[TotalBook]']     || 0;
  const overdue60     = kpi?.['[Overdue60Plus]'] || 0;
  const days180       = kpi?.['[Days180Plus]']   || 0;
  const activeDebtors = kpi?.['[ActiveDebtors]'] || 0;
  const onHoldCount   = kpi?.['[OnHoldCount]']   || 0;
  const overduePct    = totalBook > 0 ? (overdue60 / totalBook) * 100 : 0;

  return (
    <div style={{ animation:'fadeUp 0.4s ease both' }}>

      {/* Page header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:10 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--page-muted)' }}>Debtors</div>
        <div style={{ display:'flex', gap:6 }}>
          {['overview','accounts'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              fontFamily:'var(--font-body)', fontSize:11, fontWeight: view===v ? 700 : 500,
              padding:'5px 14px', borderRadius:3, cursor:'pointer',
              border: view===v ? '1px solid var(--border-gold)' : '1px solid var(--border)',
              background: view===v ? 'var(--gold)' : 'transparent',
              color: view===v ? 'var(--void)' : 'var(--text-sec)',
            }}>{v === 'overview' ? 'Overview' : 'All Accounts'}</button>
          ))}
        </div>
      </div>

      {view === 'overview' ? (
        <OverviewView kpi={kpi} l0={l0} e0={e0} r0={r0} buckets={buckets} l1={l1} e1={e1} r1={r1}
          accounts={accounts} l2={l2} e2={e2} r2={r2}
          totalBook={totalBook} overdue60={overdue60} days180={days180}
          activeDebtors={activeDebtors} onHoldCount={onHoldCount} overduePct={overduePct}
          isMobile={isMobile} />
      ) : (
        <AccountsView accounts={accounts} loading={l2} error={e2} reload={r2}
          totalBook={totalBook} isMobile={isMobile} />
      )}
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────
function OverviewView({ kpi, l0, e0, r0, buckets, l1, e1, r1, accounts, l2, e2, r2,
  totalBook, overdue60, days180, activeDebtors, onHoldCount, overduePct, isMobile }) {

  const cols = isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)';

  return (
    <>
      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:cols, gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2,3].map(i => <div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:90 }}><Spinner/></div>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
            <KpiCard label="Total Debtors Book"   value={fmtR(totalBook)}   sub={`${activeDebtors} active accounts`} />
            <KpiCard label="Overdue 60+ Days"     value={fmtR(overdue60)}   sub={`${overduePct.toFixed(1)}% of book`} subColor={overduePct > 30 ? RED : overduePct > 15 ? AMBER : GREEN} alert={overduePct > 30} />
            <KpiCard label="180+ Days (Bad Debt Risk)" value={fmtR(days180)} sub={fmtPct(days180, totalBook) + ' of book'} subColor={days180 > 0 ? RED : GREEN} alert={days180 > 0} />
            <KpiCard label="On Hold Accounts"     value={onHoldCount}       sub="with outstanding balance" subColor={onHoldCount > 0 ? AMBER : GREEN} />
          </>
        }
      </div>

      {/* Ageing chart + top overdue */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1.4fr)', gap:12, marginBottom:12 }}>
        <Card>
          <CardHeader title="Ageing Bucket Analysis" />
          <div style={{ padding:'16px 20px 20px' }}>
            {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/>
              : buckets?.length > 0 ? (
                <>
                  <BucketBar data={buckets} />
                  {/* Bucket legend with amounts */}
                  <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:6 }}>
                    {(buckets || []).map((b, i) => {
                      const pct = totalBook > 0 ? (b['[Amount]'] / totalBook) * 100 : 0;
                      const isOverdue = !['Current','30 Days'].includes(b['[Bucket]']);
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:8, height:8, borderRadius:2, background: isOverdue ? (pct > 15 ? RED : AMBER) : GREEN, flexShrink:0 }}/>
                          <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', width:70 }}>{b['[Bucket]']}</span>
                          <div style={{ flex:1, height:4, background:'var(--card-border)', borderRadius:2 }}>
                            <div style={{ height:'100%', borderRadius:2, width:`${Math.min(100,pct)}%`, background: isOverdue ? (pct > 15 ? RED : AMBER) : GREEN }}/>
                          </div>
                          <span style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:600, color: isOverdue ? (pct > 10 ? RED : 'var(--page-text)') : 'var(--page-text)', minWidth:70, textAlign:'right' }}>{fmtR(b['[Amount]'])}</span>
                          <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', minWidth:38, textAlign:'right' }}>{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : <div style={{ padding:24, textAlign:'center', color:'var(--page-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>No data</div>
            }
          </div>
        </Card>

        <Card>
          <CardHeader title="Top Overdue Accounts (60+ Days)" />
          {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/>
            : (accounts || []).filter(a => a['[Overdue]'] > 0).slice(0, 8).map((a, i) => {
              const overduePctAcc = a['[Total]'] > 0 ? (a['[Overdue]'] / a['[Total]']) * 100 : 0;
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 20px', borderBottom:'1px solid var(--card-border)' }}>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', width:18, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:2 }}>{a['[Name]']}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)' }}>
                      Total: {fmtR(a['[Total]'])} · Last pay: {fmtDate(a['[LastPay]'])}
                      {a['[OnHold]'] === 'Y' && <span style={{ marginLeft:6, color:AMBER }}>● ON HOLD</span>}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:700, color:RED }}>{fmtR(a['[Overdue]'])}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:RED }}>{overduePctAcc.toFixed(0)}% overdue</div>
                  </div>
                </div>
              );
            })
          }
        </Card>
      </div>
    </>
  );
}

// ── All Accounts ─────────────────────────────────────────────
function AccountsView({ accounts, loading, error, reload, totalBook, isMobile }) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('Total');

  const filtered = (accounts || [])
    .filter(a => !search || a['[Name]'].toLowerCase().includes(search.toLowerCase()) || a['[Account]'].toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b[`[${sortBy}]`] || 0) - (a[`[${sortBy}]`] || 0));

  const sortBtn = (label, key) => (
    <button onClick={() => setSortBy(key)} style={{
      fontFamily:'var(--font-mono)', fontSize:9, padding:'4px 10px', cursor:'pointer', borderRadius:2,
      border: sortBy===key ? '1px solid var(--border-gold)' : '1px solid var(--border)',
      background: sortBy===key ? 'var(--gold)' : 'transparent',
      color: sortBy===key ? 'var(--void)' : 'var(--text-muted)',
    }}>{label}</button>
  );

  return (
    <Card>
      {/* Search + sort toolbar */}
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--card-border)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search account or name..."
          style={{ flex:1, minWidth:180, padding:'7px 12px', background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, color:'var(--page-text)', fontFamily:'var(--font-body)', fontSize:12, outline:'none' }}
        />
        <div style={{ display:'flex', gap:5, flexShrink:0 }}>
          {sortBtn('Total','Total')}
          {sortBtn('Overdue','Overdue')}
          {sortBtn('180d','Days180')}
        </div>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', flexShrink:0 }}>{filtered.length} accounts</span>
      </div>

      {loading ? <Spinner/> : error ? <ErrorBox msg={error} onRetry={reload}/> : (
        <>
          {/* Header row */}
          {!isMobile && (
            <div style={{ display:'grid', gridTemplateColumns:'180px 1fr 90px 80px 80px 80px 80px 80px 90px 80px', gap:8, padding:'8px 16px', background:'var(--void)', borderBottom:'1px solid var(--card-border)' }}>
              {['ACCOUNT','NAME','TOTAL','CURRENT','30D','60D','90D','120D+','OVERDUE','HOLD'].map(h => (
                <div key={h} style={{ fontFamily:'var(--font-mono)', fontSize:8, letterSpacing:'0.1em', color:'var(--page-muted)', textAlign: h==='NAME' ? 'left' : 'right' }}>{h}</div>
              ))}
            </div>
          )}

          {filtered.map((a, i) => {
            const overdue = a['[Overdue]'] || 0;
            const days120plus = (a['[Days120]']||0) + (a['[Days150]']||0) + (a['[Days180]']||0);
            const isRed = overdue / (a['[Total]']||1) > 0.5;
            return isMobile ? (
              <div key={i} style={{ padding:'12px 16px', borderBottom:'1px solid var(--card-border)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--page-text)' }}>{a['[Name]']}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', marginTop:2 }}>{a['[Account]']} · Last pay: {fmtDate(a['[LastPay]'])}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'var(--page-text)' }}>{fmtR(a['[Total]'])}</div>
                    {overdue > 0 && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:RED }}>{fmtR(overdue)} overdue</div>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {[['Current',a['[Current]'],GREEN],['30d',a['[Days30]'],GOLD],['60d',a['[Days60]'],AMBER],['90d',a['[Days90]'],RED],['120d+',days120plus,RED]].filter(([,v]) => v > 0).map(([l,v,c]) => (
                    <div key={l} style={{ fontFamily:'var(--font-mono)', fontSize:9, color:c }}>{l}: {fmtR(v)}</div>
                  ))}
                  {a['[OnHold]']==='Y' && <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:AMBER }}>ON HOLD</div>}
                </div>
              </div>
            ) : (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'180px 1fr 90px 80px 80px 80px 80px 80px 90px 80px', gap:8, padding:'9px 16px', borderBottom:'1px solid var(--card-border)', background: i%2===0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a['[Account]']}</div>
                <div style={{ fontSize:12, color:'var(--page-text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a['[Name]']}{a['[OnHold]']==='Y' && <span style={{ marginLeft:6, fontFamily:'var(--font-mono)', fontSize:8, color:AMBER }}>HOLD</span>}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:600, color:'var(--page-text)', textAlign:'right' }}>{fmtR(a['[Total]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:GREEN, textAlign:'right' }}>{fmtR(a['[Current]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:a['[Days30]']>0 ? GOLD : 'var(--page-muted)', textAlign:'right' }}>{fmtR(a['[Days30]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:a['[Days60]']>0 ? AMBER : 'var(--page-muted)', textAlign:'right' }}>{fmtR(a['[Days60]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:a['[Days90]']>0 ? RED : 'var(--page-muted)', textAlign:'right' }}>{fmtR(a['[Days90]'])}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:days120plus>0 ? RED : 'var(--page-muted)', textAlign:'right' }}>{fmtR(days120plus)}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:700, color:overdue>0 ? (isRed ? RED : AMBER) : 'var(--page-muted)', textAlign:'right' }}>{fmtR(overdue)}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, textAlign:'right', color:a['[OnHold]']==='Y' ? AMBER : 'var(--page-muted)' }}>{a['[OnHold]']==='Y' ? 'YES' : '—'}</div>
              </div>
            );
          })}
        </>
      )}
    </Card>
  );
}
